import { useMemo } from "react";
import { Link } from "react-router-dom";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Cabine } from "../componentes/FerramentaUi";
import { DiaEmBloco } from "../componentes/PeritoAgenda";
import { BarrasPorMes, Ranking } from "../componentes/PeritoGraficos";
import { Atalho, Chip, EtapaProgresso, FeedDeAlertas, IconeEstrela, IconeMarcador, Secao } from "../componentes/PeritoUi";
import type { Alerta } from "../lib/perito/alertas";
import { diaDe, diasAte, formatarData } from "../lib/perito/datas";
import { recebidoNoMes, serieMensal, todosOsHonorarios, totaisDeHonorarios } from "../lib/perito/financeiro";
import { formatBRL, plural } from "../lib/perito/format";
import { etapaAtiva } from "../lib/perito/pipeline";
import { rotuloDias, tomDosDias } from "../lib/perito/proximaAcao";
import { usePerito } from "../lib/perito/store";
import { nomeDaPericia } from "./PeritoPericias";

// O painel da Central do Perito (fase B), portado de `src/routes/_authenticated/index.tsx`: os números da
// conta, os próximos prazos, as perícias em andamento, os honorários por mês, as perícias por tipo, as
// favoritas e os alertas. Toda conta é de `lib/perito/` (provada em `tests/perito-painel.mjs`); a tela só
// arruma.
//
// O que saiu de lá, de propósito: a rosca da "situação das perícias". O status é DERIVADO da etapa e tem dois
// valores (em andamento e finalizada), e o primeiro número da cabine já diz quantas estão ativas de quantas
// cadastradas; a rosca repetiria esse número em duas fatias. E a saudação com o nome: o nome está no Topo do
// hub, logo acima.

const RAIZ = "/ferramenta/central-do-perito";

export default function PeritoPainel({ alertas }: { alertas: Alerta[] }) {
  const { pericias, prazos, tarefas, filtrosSalvos } = usePerito();

  const numeros = useMemo(() => {
    const abertos = prazos.filter((p) => !p.cumprido).map((p) => diasAte(p.data)).filter((d): d is number => d !== null);
    const abertas = tarefas.filter((t) => t.status !== "concluida");
    return {
      ativas: pericias.filter(etapaAtiva).length,
      prazosNaSemana: abertos.filter((d) => d >= 0 && d <= 7).length,
      prazosVencidos: abertos.filter((d) => d < 0).length,
      aReceber: totaisDeHonorarios(todosOsHonorarios(pericias)).aReceber,
      recebidoNoMes: recebidoNoMes(pericias),
      tarefasAbertas: abertas.length,
      tarefasAtrasadas: abertas.filter((t) => (diasAte(t.prazo) ?? 0) < 0).length,
    };
  }, [pericias, prazos, tarefas]);

  // Os prazos em aberto, do que venceu há mais tempo ao mais distante: o vencido é o que pede ação primeiro.
  const proximos = useMemo(
    () => prazos
      .filter((p) => !p.cumprido && diaDe(p.data))
      .map((p) => ({ p, dias: diasAte(p.data) ?? 0, pericia: pericias.find((x) => x.id === p.pericia_id) }))
      .sort((a, b) => a.dias - b.dias || a.p.id.localeCompare(b.p.id))
      .slice(0, 5),
    [prazos, pericias],
  );

  const emAndamento = useMemo(() => pericias.filter(etapaAtiva).slice(0, 4).map((p) => {
    const prazo = prazos
      .filter((z) => z.pericia_id === p.id && !z.cumprido && diaDe(z.data))
      .sort((a, b) => (diaDe(a.data) ?? "").localeCompare(diaDe(b.data) ?? ""))[0];
    return { p, prazo, autor: p.partes.find((x) => x.tipo === "autor")?.nome, reu: p.partes.find((x) => x.tipo === "reu")?.nome };
  }), [pericias, prazos]);

  const serie = useMemo(() => serieMensal(pericias, 6), [pericias]);
  const semHonorarios = serie.every((m) => m.previsto === 0 && m.recebido === 0);

  const porTipo = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of pericias) m.set(p.tipoPericia || "Tipo não informado", (m.get(p.tipoPericia || "Tipo não informado") ?? 0) + 1);
    const total = pericias.length || 1;
    return [...m.entries()]
      .map(([nome, valor]) => ({ nome, valor, detalhe: `${Math.round((valor / total) * 100)}%` }))
      .sort((a, b) => b.valor - a.valor || a.nome.localeCompare(b.nome));
  }, [pericias]);

  const favoritas = pericias.filter((p) => p.favorito).slice(0, 6);
  const semPericias = pericias.length === 0;

  // O resumo em FRASE, só com os números que existem: "0 prazos e R$ 0,00" lê como relatório vazio.
  const partes = [
    numeros.ativas > 0 && plural(numeros.ativas, "perícia em andamento", "perícias em andamento"),
    numeros.prazosNaSemana > 0 && plural(numeros.prazosNaSemana, "prazo nesta semana", "prazos nesta semana"),
    numeros.aReceber > 0 && `${formatBRL(numeros.aReceber)} a receber`,
  ].filter(Boolean) as string[];
  const resumo = semPericias
    ? "Comece pela primeira perícia: o cadastro tem seis passos, e o número do processo já preenche o tribunal."
    : partes.length > 0
      ? `Você tem ${partes.length > 1 ? `${partes.slice(0, -1).join(", ")} e ${partes.at(-1)}` : partes[0]}.`
      : "Nenhuma perícia em andamento agora. As finalizadas continuam na lista das perícias.";

  return (
    <div className="flex flex-col gap-5" data-painel>
      <Cabine rotulo="Painel" titulo="Central do Perito" descricao={<span data-resumo>{resumo}</span>}
        acao={semPericias ? (
          <Link to={`${RAIZ}/pericias/nova`} className="pill-cta inline-flex min-h-11 items-center px-5 text-sm">Cadastrar a primeira perícia</Link>
        ) : (
          <Link to={`${RAIZ}/prazos`} className="btn-contorno-navy">Abrir a agenda</Link>
        )}
        fatos={semPericias ? undefined : (
          <>
            <TileDeFato rotulo="Perícias ativas" valor={<span data-fato="ativas">{numeros.ativas}</span>} apoio={`de ${plural(pericias.length, "cadastrada", "cadastradas")}`} />
            <TileDeFato rotulo="Prazos em 7 dias" valor={<span data-fato="prazos-na-semana">{numeros.prazosNaSemana}</span>}
              apoio={numeros.prazosVencidos > 0 ? `e ${plural(numeros.prazosVencidos, "vencido", "vencidos")}` : "nenhum vencido"} />
            {numeros.aReceber > 0 && <TileDeFato rotulo="A receber" valor={<span data-fato="a-receber">{formatBRL(numeros.aReceber)}</span>} apoio="honorários pendentes" />}
            {numeros.recebidoNoMes > 0 && <TileDeFato rotulo="Recebido no mês" valor={<span data-fato="recebido-no-mes">{formatBRL(numeros.recebidoNoMes)}</span>} apoio="o que entrou neste mês" />}
            {numeros.tarefasAbertas > 0 && (
              <TileDeFato rotulo="Tarefas em aberto" valor={<span data-fato="tarefas-abertas">{numeros.tarefasAbertas}</span>}
                apoio={numeros.tarefasAtrasadas > 0 ? plural(numeros.tarefasAtrasadas, "atrasada", "atrasadas") : "nenhuma atrasada"} />
            )}
          </>
        )} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Secao nome="proximos-prazos" titulo="Próximos prazos" acao={<Atalho para={`${RAIZ}/prazos`}>Ver a agenda</Atalho>}>
          {proximos.length === 0 ? (
            <p className="text-sm text-cinza">Nenhum prazo em aberto. Cadastre os prazos na ficha de cada perícia ou na agenda.</p>
          ) : (
            <ul className="-my-1 divide-y divide-borda">
              {proximos.map(({ p, dias, pericia }) => (
                <li key={p.id} className="flex items-center gap-3 py-2.5" data-proximo-prazo={p.id}>
                  <DiaEmBloco dia={diaDe(p.data)!} />
                  <span className="min-w-0 flex-1">
                    <span className="sr-only">{formatarData(p.data)}: </span>
                    <span className="block truncate text-sm font-semibold text-titulo">{p.titulo}</span>
                    {pericia ? (
                      <Link to={`${RAIZ}/pericias/${pericia.id}`} className="block truncate text-xs font-semibold text-link hover:underline">{nomeDaPericia(pericia)}</Link>
                    ) : (
                      <span className="block truncate text-xs text-cinza">Sem perícia vinculada</span>
                    )}
                  </span>
                  <Chip tom={tomDosDias(dias)}>{rotuloDias(dias)}</Chip>
                </li>
              ))}
            </ul>
          )}
        </Secao>

        <Secao nome="em-andamento" titulo="Perícias em andamento" acao={<Atalho para={`${RAIZ}/pericias`}>Ver todas</Atalho>}>
          {emAndamento.length === 0 ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-cinza">Nenhuma perícia em andamento. Cadastre uma para acompanhar o processo pelas 10 etapas.</p>
              <Link to={`${RAIZ}/pericias/nova`} className="pill-cta inline-flex min-h-10 items-center px-4 text-sm">Nova perícia</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {emAndamento.map(({ p, prazo, autor, reu }) => (
                <Link key={p.id} to={`${RAIZ}/pericias/${p.id}`} className="porta flex flex-col gap-2 rounded-cf border border-borda bg-superficie p-3.5" data-em-andamento={p.id}>
                  <span className="truncate text-xs text-cinza tabular-nums">{p.numeroProcesso || "Sem número"}</span>
                  <span className="-mt-1 block truncate font-display text-sm font-bold text-titulo">{nomeDaPericia(p)}</span>
                  {(autor || reu) && <span className="-mt-1 block truncate text-xs text-cinza">{autor ?? "—"} × {reu ?? "—"}</span>}
                  <EtapaProgresso pericia={p} />
                  <span className="mt-auto pt-1 text-xs font-semibold text-link">{prazo ? `${(diasAte(prazo.data) ?? 0) < 0 ? "Prazo vencido em" : "Próximo prazo:"} ${formatarData(prazo.data)}` : "Sem prazo cadastrado"}</span>
                </Link>
              ))}
            </div>
          )}
        </Secao>
      </div>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Secao nome="honorarios-por-mes" titulo="Honorários nos últimos 6 meses" descricao="O previsto pelo mês do vencimento, e o recebido pelo mês em que entrou."
          acao={<Atalho para={`${RAIZ}/financeiro`}>Ver o financeiro</Atalho>}>
          {semHonorarios ? (
            <p className="text-sm text-cinza">Ainda não há honorário com vencimento ou recebimento nestes meses. Lance os honorários na ficha de cada perícia.</p>
          ) : (
            <BarrasPorMes serie={serie} />
          )}
        </Secao>
        <Secao nome="por-tipo" titulo="Perícias por tipo">
          {porTipo.length === 0 ? <p className="text-sm text-cinza">A distribuição por tipo aparece depois do primeiro cadastro.</p> : <Ranking itens={porTipo} />}
        </Secao>
      </div>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Secao nome="favoritas" titulo="Favoritas e filtros salvos" acao={<Atalho para={`${RAIZ}/pericias`}>Ver as perícias</Atalho>}>
            {favoritas.length === 0 && filtrosSalvos.length === 0 ? (
              <p className="text-sm text-cinza">Marque perícias com a estrela e salve filtros na lista das perícias para abri-los daqui.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {favoritas.length > 0 && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {favoritas.map((p) => (
                      <Link key={p.id} to={`${RAIZ}/pericias/${p.id}`} className="porta flex items-start gap-3 rounded-cf border border-borda bg-superficie p-3.5" data-favorita={p.id}>
                        <IconeEstrela cheia className="mt-0.5 size-4 shrink-0 text-laranja-texto" />
                        <span className="min-w-0">
                          <span className="block truncate font-display text-sm font-bold text-titulo">{nomeDaPericia(p)}</span>
                          <span className="block truncate text-xs text-cinza tabular-nums">{p.numeroProcesso}</span>
                          <span className="block truncate text-xs text-cinza">{p.tribunal}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
                {filtrosSalvos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {filtrosSalvos.map((f) => (
                      <Link key={f.id} to={`${RAIZ}/pericias?filtro=${f.id}`} data-filtro-rapido={f.id}
                        className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-borda px-3.5 text-sm font-semibold text-titulo transition hover:border-acento">
                        <IconeMarcador className="size-3.5 text-laranja-texto" /> {f.nome}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Secao>
        </div>
        <Secao nome="alertas" titulo="Alertas" acao={<Atalho para={`${RAIZ}/alertas`}>Ver todos</Atalho>}>
          <FeedDeAlertas raiz={RAIZ} alertas={alertas} limite={5} />
        </Secao>
      </div>
    </div>
  );
}
